import { useState } from 'react'
import {
  Box,
  IconButton,
  Popover,
  Slider,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Button,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

export type BoundaryMode = 'ceiling' | 'boxed'
export type XBoundaryMode = 'bounce' | 'wrap'

export interface PhysicsSettings {
  friction: number
  maxIcons: number
  currentIcons: number
  spawnPerSecond: number
  minSpeed: number
  maxSpeed: number
  bounceStrength: number
  mouseRadius: number
  mousePushForce: number
  mouseMomentumTransfer: number
  boundaryMode: BoundaryMode
  gravity: number
  xBoundaryMode: XBoundaryMode
}

interface SettingsPanelProps {
  settings: PhysicsSettings
  onSettingsChange: (settings: Partial<PhysicsSettings>) => void
  onReset?: () => void
}

const DEFAULT_SETTINGS: Omit<PhysicsSettings, 'currentIcons'> = {
  friction: 0.99,
  maxIcons: 120,
  spawnPerSecond: 1,
  minSpeed: 0.3,
  maxSpeed: 15,
  bounceStrength: 0.8,
  mouseRadius: 100,
  mousePushForce: 2,
  mouseMomentumTransfer: 0.8,
  boundaryMode: 'ceiling',
  gravity: 0,
  xBoundaryMode: 'bounce',
}

export function SettingsPanel({ settings, onSettingsChange, onReset }: SettingsPanelProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const handleChange = (key: keyof PhysicsSettings) => (_: Event, value: number | number[]) => {
    onSettingsChange({ [key]: value as number })
  }

  const handleReset = () => {
    onSettingsChange(DEFAULT_SETTINGS)
    onReset?.()
  }

  return (
    <>
      {/* Settings Icon */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 100,
        }}
      >
        <IconButton
          onClick={handleClick}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <SettingsIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>

      {/* Settings Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'rgba(15, 23, 42, 0.98)',
              backdropFilter: 'blur(20px)',
              color: 'white',
              width: 400,
              maxHeight: '80vh',
              mt: 1,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Physics Settings
            </Typography>
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                },
              }}
              variant="outlined"
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
          <Stack spacing={3} sx={{ pt: 2 }}>
            {/* X Boundary Mode */}
            <Box>
              <Typography variant="body2" gutterBottom>
                X Walls
              </Typography>
              <ToggleButtonGroup
                value={settings.xBoundaryMode}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    onSettingsChange({ xBoundaryMode: value as XBoundaryMode })
                  }
                }}
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="bounce">
                  Bounce
                </ToggleButton>
                <ToggleButton value="wrap">
                  Wrap
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Y Boundary Mode */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Y Walls
              </Typography>
              <ToggleButtonGroup
                value={settings.boundaryMode}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    onSettingsChange({ boundaryMode: value as BoundaryMode })
                  }
                }}
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="ceiling">
                  Wrap
                </ToggleButton>
                <ToggleButton value="boxed">
                  Bounce
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Friction */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Friction: {settings.friction.toFixed(2)}
              </Typography>
              <Slider
                value={settings.friction}
                onChange={handleChange('friction')}
                min={0.9}
                max={1.0}
                step={0.01}
                marks={[
                  { value: 0.9, label: '0.90' },
                  { value: 0.95, label: '0.95' },
                  { value: 1.0, label: '1.00' },
                ]}
              />
            </Box>

            {/* Max Icons */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Max Icons: {settings.maxIcons}
              </Typography>
              <Slider
                value={settings.maxIcons}
                onChange={handleChange('maxIcons')}
                min={30}
                max={300}
                step={10}
                marks={[
                  { value: 30, label: '30' },
                  { value: 120, label: '120' },
                  { value: 300, label: '300' },
                ]}
              />
            </Box>

            {/* Current Icons (Read-only) */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Current Icons: {settings.currentIcons}
              </Typography>
              <Slider
                value={settings.currentIcons}
                min={0}
                max={settings.maxIcons}
                disabled
              />
            </Box>

            {/* Spawn Rate */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Spawn Rate: {settings.spawnPerSecond.toFixed(1)} icons/sec
              </Typography>
              <Slider
                value={settings.spawnPerSecond}
                onChange={handleChange('spawnPerSecond')}
                min={0}
                max={5}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                ]}
              />
            </Box>

            {/* Min Speed */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Min Speed: {settings.minSpeed.toFixed(1)}
              </Typography>
              <Slider
                value={settings.minSpeed}
                onChange={handleChange('minSpeed')}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                ]}
              />
            </Box>

            {/* Max Speed */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Max Speed: {settings.maxSpeed.toFixed(0)}
              </Typography>
              <Slider
                value={settings.maxSpeed}
                onChange={handleChange('maxSpeed')}
                min={5}
                max={30}
                step={1}
                marks={[
                  { value: 5, label: '5' },
                  { value: 15, label: '15' },
                  { value: 30, label: '30' },
                ]}
              />
            </Box>

            {/* Bounce Strength */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Bounce Strength: {settings.bounceStrength.toFixed(2)}
              </Typography>
              <Slider
                value={settings.bounceStrength}
                onChange={handleChange('bounceStrength')}
                min={0.1}
                max={1.0}
                step={0.05}
                marks={[
                  { value: 0.1, label: '0.1' },
                  { value: 0.5, label: '0.5' },
                  { value: 1.0, label: '1.0' },
                ]}
              />
            </Box>

            {/* Mouse Radius */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Mouse Radius: {settings.mouseRadius}
              </Typography>
              <Slider
                value={settings.mouseRadius}
                onChange={handleChange('mouseRadius')}
                min={50}
                max={300}
                step={10}
                marks={[
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 300, label: '300' },
                ]}
              />
            </Box>

            {/* Mouse Push Force */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Mouse Push Force: {settings.mousePushForce.toFixed(1)}
              </Typography>
              <Slider
                value={settings.mousePushForce}
                onChange={handleChange('mousePushForce')}
                min={0}
                max={10}
                step={0.5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 2, label: '2' },
                  { value: 10, label: '10' },
                ]}
              />
            </Box>

            {/* Mouse Momentum Transfer */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Mouse Momentum: {settings.mouseMomentumTransfer.toFixed(1)}
              </Typography>
              <Slider
                value={settings.mouseMomentumTransfer}
                onChange={handleChange('mouseMomentumTransfer')}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.8, label: '0.8' },
                  { value: 2, label: '2' },
                ]}
              />
            </Box>

            {/* Gravity */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Gravity: {settings.gravity.toFixed(2)}
              </Typography>
              <Slider
                value={settings.gravity}
                onChange={handleChange('gravity')}
                min={0}
                max={0.5}
                step={0.01}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.1, label: '0.1' },
                  { value: 0.5, label: '0.5' },
                ]}
              />
            </Box>

          </Stack>

          {/* Enable username/password login */}
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              window.location.href = '/login?allowUsernamePasswordLogin=true'
            }}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Enable username / password login
          </Button>
        </Box>
      </Popover>
    </>
  )
}
